export type ExportMediaType = 'photo' | 'video' | 'other';

/** View-neutral description of one album asset. Built by the adapters in exportItems.ts. */
export interface ExportSource {
  id: string;
  originalUrl: string | null;
  thumbnailUrl: string | null;
  mediaType: ExportMediaType;
  createdAt: string | null;
}

export interface ExportItem {
  id: string;
  /** URL of the original file. */
  url: string;
  /** Storage basename, unchanged. Unique within one export. */
  filename: string;
  thumbnailUrl: string | null;
  mediaType: ExportMediaType;
  createdAt: string | null;
  /** Bytes, from a HEAD request. null until measured or when the server did not say. */
  size: number | null;
}

export type ExportOutcome = 'complete' | 'cancelled' | 'fatal';

export interface ExportSink {
  /** True when the item is already present and can be skipped. */
  has(item: ExportItem): Promise<boolean>;
  write(item: ExportItem, signal: AbortSignal): Promise<void>;
  /** Called once per run, after every item has settled or the run stopped. */
  finish(outcome: ExportOutcome): Promise<void>;
}

export interface ExportProgress {
  total: number;
  done: number;
  saved: number;
  skipped: number;
  failed: number;
}

export interface ExportResult {
  saved: ExportItem[];
  skipped: ExportItem[];
  failed: ExportItem[];
  outcome: ExportOutcome;
  fatalMessage: string | null;
}

/** Thrown by a sink when continuing is pointless (disk full, folder unwritable). */
export class ExportFatalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExportFatalError';
  }
}

export type FetchFn = typeof fetch;

/** The slice of the File System Access API the folder sink uses. Real handles satisfy these. */
export interface FsWritable {
  write(data: Uint8Array | Blob | string): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}
export interface FsFileHandle {
  getFile(): Promise<{ size: number }>;
  createWritable(): Promise<FsWritable>;
}
export interface FsDirHandle {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FsDirHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FsFileHandle>;
  removeEntry(name: string): Promise<void>;
}
